import React, {useState} from 'react';

import {BaseLinkModel} from "../../../infrastructure/models/link/base-link-model";
import {NavigationManager} from "../../managers/navigation-manager";
import {BaseProps} from "../../../infrastructure/models/props/base-props";
import {LINK_TYPES, SITE_DOMAINS, LINK_TARGET, ROUTE_NAMES, ROUTE_TYPES, LINK_REL} from "../../consts";
import {BaseLinkModelSettings} from "../../../infrastructure/models/link/base-link-model-settings";
import {UseIoC} from "../composers/composer-plugins/use-ioc";
import {RouterController} from "../router/router-controller";
import {RouteModel} from "../router/models/route-model";
import {AppModuleState} from "../../app/models/app-module.state";
import {IOC} from "../../../infrastructure/ioc";
import {DomUtils} from "../../../infrastructure/utils/dom-utils";
import {LinkToModel} from "./link.to-model";
import {BaseUrlModel} from "../../../infrastructure/models/link/base-url-model";

export class LinkProps extends BaseProps {
  model: BaseLinkModel;
  replace: Boolean;
  shouldForcePropagate: Boolean;
  noFollow: Boolean;
  disable: Boolean = false;
  shouldScrollToTop: Boolean = false;
  shouldStopPropagation: Boolean = false;
  className: String;
  style: Object;
  routeProps: Object;
  onMouseEnter: Function;
  onMouseLeave: Function;
  ioc: IOC;
  onClick: Function;
}

export const LinkComposer = (Link) => UseIoC()((props: LinkProps) => {
  const navigationManager: NavigationManager = props.ioc.getInstance(NavigationManager);
  const routerController: RouterController = props.ioc.getInstance(RouterController);
  const appState: AppModuleState = props.ioc.getInstance(AppModuleState);
  const {eventClick, blockInnerLinks, use365Prefix} = appState.settings.bootstrapSettings;

  const isSponsoredLink = props.rel === LINK_REL.SPONSORED_NOOPENER;
  const shouldUse365Prefix: Boolean = appState.settings.isWidget && use365Prefix && !isSponsoredLink;
  const [updateTime, setUpdateTime] = useState(Date.now());

  const model: BaseLinkModel = props.model;
  const isSpanClickable = !model?.isLinkTag || (appState.settings.isWidget && !blockInnerLinks) || model.replace;
  const noFollow = props.noFollow || model?.isFollow === false ? 'nofollow' : '';
  const rel: String = !isSponsoredLink ? noFollow : props.rel;
  const isAnchor = model instanceof BaseUrlModel;

  const onLinkClick = (e) => {
    const model: BaseLinkModel = props.model;
    let enableLink = true;

    if (!model || props.disable) {
      return props.onClick?.();
    }

    setUpdateTime(Date.now());
    const settings: BaseLinkModelSettings = model.settings;
    const routeModel: RouteModel = routerController.findRouteByName(model.name);

    if (settings && settings.fullRefresh) {
      setTimeout(() => window.location.reload(), 0);
    }

    if (props.shouldScrollToTop && !shouldUse365Prefix && !blockInnerLinks) {
      scrollToTop();
    }

    if (props.shouldStopPropagation) {
      e.stopPropagation()
    }

    //check if in use
    if (model.type === LINK_TYPES.external) {
      window.location = navigationManager.createUrl(routeModel.path, model, true);
      enableLink = false;
    }

    if (model.onClick) {
      enableLink = model.onClick(e, model);
    }

    if (isSpanClickable) {
      if (!shouldUse365Prefix || !model.isLinkTag || model.replace) {
        const isUrlModel: Boolean = model instanceof BaseUrlModel;

        isUrlModel ? navigationManager.gotoUrl(model.toString(), model.replace, true) : navigationManager.gotoRoute(routeModel, model, model.replace);
      } else if (model.isLinkTag && shouldUse365Prefix) {
        const isDashboardPage: Boolean = model.name === ROUTE_NAMES.dashboardPage;
        const modelName: String = isDashboardPage ? ROUTE_NAMES.allScoresPage : model.name;
        const routeModel: RouteModel = routerController.findRouteByName(modelName);
        const isNestedRoute: Boolean = routeModel?.config?.routeType === ROUTE_TYPES.nested;
        const targetPath: String = isNestedRoute ? routeModel.parentPath : routeModel.path;
        const url: String = `${SITE_DOMAINS.full}${navigationManager.createUrl(targetPath, model, true)}`

        navigationManager.gotoUrl(url, true, true)
      }
    }

    if (props.onClick) {
      enableLink = props.onClick(e)
    }

    if (eventClick) {
      enableLink = eventClick(model);
    }

    if (!enableLink) {
      if (!props.shouldForcePropagate) {
        e.stopPropagation();
      }
      e.preventDefault();

      enableLink = !model.settings?.disabled;
    }

    if ((window.location.pathname === to && !settings) || model.settings?.disabled) {
      enableLink = false;
    }

    return enableLink;
  }

  if (!model) {
    return <Link {...props} onLinkClick={props.onClick} disabled={true}/>
  }

  const routeModel: RouteModel = routerController.findRouteByName(model.name);
  const shouldUseParentPath: Boolean = routeModel?.parent && routeModel.parent !== navigationManager.currentLinkModel?.name;
  const isNestedRoute: Boolean = routeModel?.config?.routeType === ROUTE_TYPES.nested;
  const targetPath: String = isNestedRoute || shouldUseParentPath ? routeModel.parentPath : routeModel?.path;
  const pathname: String = routeModel? navigationManager.createUrl(targetPath, model, false) : model.url || '/';
  const hash: String = model.hash;
  const search: String = model.search;
  const isFormalRoute: Boolean = routerController.isFormalRoute(routeModel);
  const replace: Boolean = props.replace || model.replace || (routeModel && !isFormalRoute);
  const settings: BaseLinkModelSettings = model && model.settings;

  const onMouseEnter = () => {
    props.onMouseEnter && props.onMouseEnter();
    routeModel && routerController.preloadRouteComponent(routeModel.name)
  }

  settings && (settings.updateTime = updateTime);

  const to: LinkToModel = new LinkToModel(pathname, hash, search)

  const enabled: Boolean = !props.disable && model?.isLinkTag && (!model.settings?.disabled || !blockInnerLinks) && !appState.settings.isWidget //TODO: use enabled
  const target: LINK_TARGET = settings?.shouldOpenNewTab ? LINK_TARGET.newTab : null;

  const widgetProps = {
    ...props,
    onLinkClick,
    onMouseEnter,
    to: to.toString(),
    rel,
    replace,
    enabled,
    isAnchor,
    target,
    isClickable: isSpanClickable || enabled,
    state: {
      ...(settings || {}),
      ...(props.routeProps || {})
    }
  }

  const scrollToTop = () => {
    const domUtils: DomUtils = props.ioc.getInstance(DomUtils);

    setTimeout(() => domUtils.scrollToTop(), 0);
  }

  return <Link {...widgetProps} />;
});
